import { Controller } from '@hotwired/stimulus'

const ARROW_WIDTH = 5
const ARROW_HEIGHT = 8
const TOOLTIP_MARGIN = 2

export default class extends Controller {
  // Add a static method to close all tooltips
  static closeAll() {
    window.dispatchEvent(new CustomEvent('tooltip:close-all', {
      detail: { except: null },
    }))
  }

  static values = {
    position: { type: String, default: 'top' },
    persistent: { type: Boolean, default: false },
    content: { type: String, default: '' }, // Content value
    mobile: { type: Boolean, default: false }, // Mobile visibility toggle
  }

  static targets = ['content']

  connect = () => {
    // Create elements only once during connection
    this.tooltip = document.createElement('div')
    this.arrow = document.createElement('div')

    // Store original content target if it exists
    if (this.hasContentTarget) {
      // Clone the content target to preserve the original
      this.originalContent = this.contentTarget
    }
    this.clonedContent = null

    // Check if on mobile device
    this.isMobile = window.matchMedia('(max-width: 768px)').matches

    // Set positioning on parent element
    this.element.style.position = 'relative'

    // Add event listeners based on persistence and mobile settings
    this.setupEventListeners()

    // Add resize listener to handle orientation changes
    this.resizeHandler = this.handleResize.bind(this)
    window.addEventListener('resize', this.resizeHandler)

    // Global document click handler to close any open tooltips
    this.clickHandler = this.handleDocumentClick.bind(this)
    document.addEventListener('click', this.clickHandler)

    // Store visibility state
    this.isVisible = false

    // Create a unique ID for this tooltip
    this.tooltipId = `tooltip-${Math.random().toString(36).substring(2, 11)}`
    this.tooltip.id = this.tooltipId

    // Initialize tooltip in hidden state
    this.setupTooltipStyles()
  }

  disconnect = () => {
    // Remove event listeners
    this.removeEventListeners()

    // Remove resize listener
    window.removeEventListener('resize', this.resizeHandler)

    document.removeEventListener('click', this.clickHandler)

    // Remove elements from DOM
    if (document.body.contains(this.tooltip)) {
      this.tooltip.remove()
    }
    if (document.body.contains(this.arrow)) {
      this.arrow.remove()
    }
  }

  persistentValueChanged = () => {
    // Update pointer-events when persistence value changes
    if (this.tooltip) {
      this.tooltip.style.pointerEvents = this.persistentValue ? 'auto' : 'none'
    }

    // Update event listeners
    this.removeEventListeners()
    this.setupEventListeners()
  }

  mobileValueChanged = () => {
    // Update event listeners when mobile value changes
    this.removeEventListeners()
    this.setupEventListeners()
  }

  setupEventListeners = () => {
    // Persistent tooltips should always work (including on mobile)
    if (this.persistentValue) {
      this.element.addEventListener('click', this.toggle)
      return
    }

    // For hover tooltips, check mobile settings
    if (this.isMobile && !this.mobileValue) {
      return
    }

    this.element.addEventListener('mouseenter', this.show)
    this.element.addEventListener('mouseleave', this.hide)
  }

  removeEventListeners = () => {
    // Remove all possible event listeners
    this.element.removeEventListener('click', this.toggle)
    this.element.removeEventListener('mouseenter', this.show)
    this.element.removeEventListener('mouseleave', this.hide)
  }

  handleResize = () => {
    // Update mobile status
    const wasMobile = this.isMobile
    this.isMobile = window.matchMedia('(max-width: 768px)').matches

    // If mobile status changed, update event listeners
    if (wasMobile !== this.isMobile) {
      this.removeEventListeners()
      this.setupEventListeners()

      // Hide tooltip if showing and transitioning to mobile with mobile disabled
      if (this.isMobile && !this.mobileValue && this.isVisible) {
        this.hide()
      }
    }
  }

  setupTooltipStyles = () => {
    // Initial tooltip setup
    this.tooltip.style.position = 'fixed'
    this.tooltip.style.zIndex = '999'
    this.tooltip.style.backgroundColor = 'var(--tooltip-bg, #333)'
    this.tooltip.style.color = '#fff'
    this.tooltip.style.borderRadius = '5px'
    this.tooltip.style.boxSizing = 'border-box'
    this.tooltip.style.padding = '8px 12px'
    this.tooltip.style.transition = 'opacity 0.3s'
    this.tooltip.style.opacity = '0'
    this.tooltip.style.visibility = 'hidden'
    this.tooltip.style.maxWidth = '300px'
    this.tooltip.style.pointerEvents = this.persistentValue ? 'auto' : 'none'
    this.tooltip.style.boxShadow = '0 2px 10px rgba(0, 0, 0, 0.2)'
    this.tooltip.style.fontSize = '0.8rem'

    // Initial arrow setup - very important to NOT set width/height to 0 here
    this.arrow.style.position = 'fixed'
    this.arrow.style.zIndex = '999'
    this.arrow.style.transition = 'opacity 0.3s'
    this.arrow.style.opacity = '0'
    this.arrow.style.visibility = 'hidden'
    this.arrow.style.pointerEvents = 'none'
    // The actual width/height will be set by the borders
  }

  applyArrowStyles = () => {
    // Get tooltip background color, either from CSS variable or default
    let tooltipBg = '#333' // Default background color

    try {
      // Try to get computed background color of tooltip
      const computedStyle = window.getComputedStyle(this.tooltip)
      const bgColor = computedStyle.backgroundColor

      if (bgColor && bgColor !== 'rgba(0, 0, 0, 0)' && bgColor !== 'transparent') {
        tooltipBg = bgColor
      }
    }
    catch (e) {
      console.warn('Could not get tooltip background color, using default')
      console.warn(e)
    }

    // Reset all borders and positioning
    this.arrow.style.width = '0'
    this.arrow.style.height = '0'
    this.arrow.style.display = 'block' // Ensure it's visible

    // Reset borders
    this.arrow.style.borderLeft = 'none'
    this.arrow.style.borderRight = 'none'
    this.arrow.style.borderTop = 'none'
    this.arrow.style.borderBottom = 'none'

    // Apply specific styles based on position
    switch (this.positionValue) {
      case 'top':
        // Arrow pointing down
        this.arrow.style.borderLeft = `${ARROW_WIDTH}px solid transparent`
        this.arrow.style.borderRight = `${ARROW_WIDTH}px solid transparent`
        this.arrow.style.borderTop = `${ARROW_HEIGHT}px solid ${tooltipBg}`
        break
      case 'bottom':
        // Arrow pointing up
        this.arrow.style.borderLeft = `${ARROW_WIDTH}px solid transparent`
        this.arrow.style.borderRight = `${ARROW_WIDTH}px solid transparent`
        this.arrow.style.borderBottom = `${ARROW_HEIGHT}px solid ${tooltipBg}`
        break
      case 'left':
        // Arrow pointing right
        this.arrow.style.borderTop = `${ARROW_WIDTH}px solid transparent`
        this.arrow.style.borderBottom = `${ARROW_WIDTH}px solid transparent`
        this.arrow.style.borderLeft = `${ARROW_HEIGHT}px solid ${tooltipBg}`
        break
      case 'right':
        // Arrow pointing left
        this.arrow.style.borderTop = `${ARROW_WIDTH}px solid transparent`
        this.arrow.style.borderBottom = `${ARROW_WIDTH}px solid transparent`
        this.arrow.style.borderRight = `${ARROW_HEIGHT}px solid ${tooltipBg}`
        break
    }
  }

  toggle = (event) => {
    // For persistent tooltips, toggle visibility on click
    if (this.isVisible) {
      this.hide()
    }
    else {
      // Save the target element coordinates before showing the tooltip
      // This ensures we have proper positioning when clicking small elements
      this.clickPosition = {
        left: event.target.getBoundingClientRect().left,
        top: event.target.getBoundingClientRect().top,
        right: event.target.getBoundingClientRect().right,
        bottom: event.target.getBoundingClientRect().bottom,
        width: event.target.getBoundingClientRect().width,
        height: event.target.getBoundingClientRect().height,
      }

      this.show(event)
    }

    // Prevent the click from triggering document click handler immediately
    event.stopPropagation()
  }

  show = (event) => {
    // Close any other open tooltips
    window.dispatchEvent(new CustomEvent('tooltip:close-all', {
      detail: { except: this.tooltipId },
    }))

    // For persistent tooltips with click event, store the target element position
    if (this.persistentValue && event && event.target) {
      // If clicked on a child of the controller element, store its position instead
      // This is useful when clicking on buttons or icons inside a larger container
      this.clickPosition = {
        left: event.target.getBoundingClientRect().left,
        top: event.target.getBoundingClientRect().top,
        right: event.target.getBoundingClientRect().right,
        bottom: event.target.getBoundingClientRect().bottom,
        width: event.target.getBoundingClientRect().width,
        height: event.target.getBoundingClientRect().height,
      }
    }
    else {
      this.clickPosition = null
    }

    // Clear tooltip content
    while (this.tooltip.firstChild) {
      this.tooltip.removeChild(this.tooltip.firstChild)
    }

    // Use either content target or content value
    if (this.hasContentTarget && this.contentTarget) {
      // Clone the content target for this tooltip instance
      this.clonedContent = this.contentTarget.cloneNode(true)
      this.clonedContent.style.display = 'block'
      this.tooltip.appendChild(this.clonedContent)
    }
    else if (this.contentValue) {
      // Use the content value
      this.tooltip.textContent = this.contentValue
    }
    else {
      // No content available
      console.warn('Tooltip has no content (no content target or content value)')
      return
    }

    // Add elements to DOM if they're not already there
    if (!document.body.contains(this.tooltip)) {
      document.body.appendChild(this.tooltip)
    }

    if (!document.body.contains(this.arrow)) {
      document.body.appendChild(this.arrow)
    }

    // Calculate positions after elements are in the DOM
    // Use double requestAnimationFrame for more reliable rendering
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Apply arrow styles now that tooltip is in the DOM
        this.applyArrowStyles()
        this.setPosition()

        // Make tooltip visible
        this.tooltip.style.opacity = '1'
        this.tooltip.style.visibility = 'visible'
        this.arrow.style.opacity = '1'
        this.arrow.style.visibility = 'visible'

        // Track visibility state
        this.isVisible = true

        // Add auto-hide timeout as a safety mechanism (only for non-persistent tooltips)
        if (!this.persistentValue) {
          this.autoHideTimeout = setTimeout(() => {
            this.hide()
          }, 10000) // Auto-hide after 10 seconds
        }
      })
    })

    // Listen for close-all events
    this.closeAllHandler = this.handleCloseAll.bind(this)
    window.addEventListener('tooltip:close-all', this.closeAllHandler)
  }

  hide = () => {
    // Clear auto-hide timeout if exists
    if (this.autoHideTimeout) {
      clearTimeout(this.autoHideTimeout)
      this.autoHideTimeout = null
    }

    // Remove close-all event listener
    if (this.closeAllHandler) {
      window.removeEventListener('tooltip:close-all', this.closeAllHandler)
      this.closeAllHandler = null
    }

    // Clear saved click position
    this.clickPosition = null

    this.tooltip.style.opacity = '0'
    this.tooltip.style.visibility = 'hidden'
    this.arrow.style.opacity = '0'
    this.arrow.style.visibility = 'hidden'

    // Update visibility tracking
    this.isVisible = false
  }

  handleCloseAll = (event) => {
    // Only close if this isn't the tooltip that triggered the event
    if (event.detail.except !== this.tooltipId) {
      this.hide()
    }
  }

  handleDocumentClick = (event) => {
    // Close the tooltip when clicking outside of it
    if (this.isVisible) {
      // For persistent tooltips, only close if click is outside both the element and tooltip
      if (this.persistentValue) {
        if (!this.element.contains(event.target) && !this.tooltip.contains(event.target)) {
          this.hide()
        }
      }
      else {
        // For normal tooltips, close when clicking anywhere outside the element
        if (!this.element.contains(event.target)) {
          this.hide()
        }
      }
    }
  }

  setPosition = () => {
    // Account for scroll position
    const scrollX = window.scrollX || window.pageXOffset
    const scrollY = window.scrollY || window.pageYOffset

    // Get fresh position measurements
    // For persistent tooltips that were clicked, use the saved click position
    const elmPos = this.persistentValue && this.clickPosition
      ? this.clickPosition
      : this.element.getBoundingClientRect()

    // Position tooltip first so we can get accurate measurements
    switch (this.positionValue) {
      case 'bottom':
        this.setTooltipUnderElement(elmPos, scrollX, scrollY)
        break
      case 'left':
        this.setTooltipLeftOfElement(elmPos, scrollX, scrollY)
        break
      case 'right':
        this.setTooltipRightOfElement(elmPos, scrollX, scrollY)
        break
      case 'top':
      default:
        this.setTooltipAboveElement(elmPos, scrollX, scrollY)
    }

    // After positioning, check if tooltip is outside viewport and adjust if needed
    this.ensureTooltipInViewport()

    // Position the arrow directly in relation to the element (not the tooltip)
    this.updateArrowPosition()
  }

  setTooltipUnderElement = (elmPos, scrollX, scrollY) => {
    // Position the tooltip first
    this.tooltip.style.top = `${elmPos.bottom + ARROW_HEIGHT + scrollY}px`

    // Center horizontally
    const tipWidth = this.tooltip.offsetWidth
    const center = elmPos.left + (elmPos.width - tipWidth) / 2
    this.tooltip.style.left = `${center + scrollX}px`

    // Position arrow - for bottom position, arrow should be at the top of tooltip
    this.arrow.style.top = `${elmPos.bottom + scrollY}px`
    this.arrow.style.bottom = 'auto'
    this.arrow.style.left = `${elmPos.left + (elmPos.width / 2) - ARROW_WIDTH + scrollX}px`
    this.arrow.style.right = 'auto'
  }

  setTooltipAboveElement = (elmPos, scrollX, scrollY) => {
    // Position the tooltip first
    this.tooltip.style.top = `${elmPos.top - this.tooltip.offsetHeight - ARROW_HEIGHT + scrollY}px`

    // Center horizontally
    const tipWidth = this.tooltip.offsetWidth
    const center = elmPos.left + (elmPos.width - tipWidth) / 2
    this.tooltip.style.left = `${center + scrollX}px`

    // Position arrow - for top position, arrow should be at the bottom of tooltip
    this.arrow.style.top = `${elmPos.top - ARROW_HEIGHT + scrollY}px`
    this.arrow.style.bottom = 'auto'
    this.arrow.style.left = `${elmPos.left + (elmPos.width / 2) - ARROW_WIDTH + scrollX}px`
    this.arrow.style.right = 'auto'
  }

  setTooltipLeftOfElement = (elmPos, scrollX, scrollY) => {
    // Position the tooltip first
    this.tooltip.style.left = `${elmPos.left - this.tooltip.offsetWidth - ARROW_HEIGHT + scrollX}px`

    // Center vertically
    const tipHeight = this.tooltip.offsetHeight
    const center = elmPos.top + (elmPos.height - tipHeight) / 2
    this.tooltip.style.top = `${center + scrollY}px`

    // Position arrow - for left position, arrow should be at the right of tooltip
    this.arrow.style.top = `${elmPos.top + (elmPos.height / 2) - ARROW_WIDTH + scrollY}px`
    this.arrow.style.bottom = 'auto'
    this.arrow.style.left = `${elmPos.left - ARROW_HEIGHT + scrollX}px`
    this.arrow.style.right = 'auto'
  }

  setTooltipRightOfElement = (elmPos, scrollX, scrollY) => {
    // Position the tooltip first
    this.tooltip.style.left = `${elmPos.right + ARROW_HEIGHT + scrollX}px`

    // Center vertically
    const tipHeight = this.tooltip.offsetHeight
    const center = elmPos.top + (elmPos.height - tipHeight) / 2
    this.tooltip.style.top = `${center + scrollY}px`

    // Position arrow - for right position, arrow should be at the left of tooltip
    this.arrow.style.top = `${elmPos.top + (elmPos.height / 2) - ARROW_WIDTH + scrollY}px`
    this.arrow.style.bottom = 'auto'
    this.arrow.style.left = `${elmPos.right + scrollX}px`
    this.arrow.style.right = 'auto'
  }

  ensureTooltipInViewport = () => {
    const tipRect = this.tooltip.getBoundingClientRect()
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    // Get current position values
    let left = parseInt(this.tooltip.style.left, 10)
    let top = parseInt(this.tooltip.style.top, 10)

    // Check if tooltip extends beyond right edge
    if (tipRect.right > viewportWidth - TOOLTIP_MARGIN) {
      left -= (tipRect.right - viewportWidth + TOOLTIP_MARGIN)
      this.tooltip.style.left = `${left}px`
    }

    // Check if tooltip extends beyond left edge
    if (tipRect.left < TOOLTIP_MARGIN) {
      left += (TOOLTIP_MARGIN - tipRect.left)
      this.tooltip.style.left = `${left}px`
    }

    // Check if tooltip extends beyond bottom edge
    if (tipRect.bottom > viewportHeight - TOOLTIP_MARGIN) {
      top -= (tipRect.bottom - viewportHeight + TOOLTIP_MARGIN)
      this.tooltip.style.top = `${top}px`
    }

    // Check if tooltip extends beyond top edge
    if (tipRect.top < TOOLTIP_MARGIN) {
      top += (TOOLTIP_MARGIN - tipRect.top)
      this.tooltip.style.top = `${top}px`
    }
  }

  updateArrowPosition = () => {
    const scrollX = window.scrollX || window.pageXOffset
    const scrollY = window.scrollY || window.pageYOffset

    // Get fresh position measurements - use saved click position for persistent tooltips
    const elmPos = this.persistentValue && this.clickPosition
      ? this.clickPosition
      : this.element.getBoundingClientRect()

    // Adjust arrow position based on current tooltip position
    if (this.positionValue === 'top') {
      // Top arrow should point down to the element
      this.arrow.style.left = `${elmPos.left + (elmPos.width / 2) - ARROW_WIDTH + scrollX}px`
      this.arrow.style.top = `${elmPos.top - ARROW_HEIGHT + scrollY}px`
    }
    else if (this.positionValue === 'bottom') {
      // Bottom arrow should point up to the element
      this.arrow.style.left = `${elmPos.left + (elmPos.width / 2) - ARROW_WIDTH + scrollX}px`
      this.arrow.style.top = `${elmPos.bottom + scrollY}px`
    }
    else if (this.positionValue === 'left') {
      // Left arrow should point right to the element
      this.arrow.style.left = `${elmPos.left - ARROW_HEIGHT + scrollX}px`
      this.arrow.style.top = `${elmPos.top + (elmPos.height / 2) - ARROW_WIDTH + scrollY}px`
    }
    else if (this.positionValue === 'right') {
      // Right arrow should point left to the element
      this.arrow.style.left = `${elmPos.right + scrollX}px`
      this.arrow.style.top = `${elmPos.top + (elmPos.height / 2) - ARROW_WIDTH + scrollY}px`
    }
  }
}
